import { useEffect, useState } from 'react';
import { getTranslations, getLanguageFromCache, saveLanguageToCache, getBrowserLanguage } from './i18n';
import { useTheme } from '../components/shared/ThemeProvider';
import { createSafeTranslations } from './translation-utils';
import { useNavigation } from './navigation';

/**
 * Resolves the active language (query param > cookie > browser) and keeps the cookie in sync.
 * Useful on its own for locale-aware formatting (plural rules, dates) alongside useTranslations.
 */
export function useCurrentLanguage(): string {
    const { query } = useNavigation();
    const { lang } = query;
    const [currentLang, setCurrentLang] = useState<string>('en');
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (!isInitialized) {
            const cachedLang = getLanguageFromCache();
            const browserLang = getBrowserLanguage();
            const selectedLang = (lang as string) || cachedLang || browserLang;

            setCurrentLang(selectedLang);
            saveLanguageToCache(selectedLang);
            setIsInitialized(true);
        } else if (lang) {
            const selectedLang = lang as string;
            if (selectedLang !== currentLang) {
                setCurrentLang(selectedLang);
                saveLanguageToCache(selectedLang);
            }
        }
    });

    return currentLang;
}

export function useTranslations() {
    const { theme } = useTheme();
    const currentLang = useCurrentLanguage();

    // Get global translations
    const globalTranslations = getTranslations(currentLang);

    // Get theme translations for the current language
    const themeTranslations = theme?.translations?.[currentLang] || {};

    // Merge translations with theme translations taking priority
    const mergedTranslations = mergeTranslations(globalTranslations, themeTranslations);

    // Return safe proxy that ensures all nested paths return strings
    return createSafeTranslations(mergedTranslations);
}

function mergeTranslations(global: any, theme: any): any {
    if (!theme || typeof theme !== 'object') {
        return global;
    }

    const result = { ...global };

    // Only merge the 'slogan' field from theme translations
    // All other translations come from global translations
    if (theme.slogan) {
        result.slogan = theme.slogan;
    }

    return result;
}
